import { isValidYmd, SetNameSchema } from "@nexus/contracts";

import { resolveRollingWindow } from "@/features/dashboard/lib/explore-date-presets";
import {
  DEFAULT_PERSONA_DATA_WINDOW_PRESET,
  PERSONA_DATA_WINDOW_DAYS,
  PERSONA_OPPORTUNITY_DEFAULT_DAYS_BACK,
  createDefaultPersonaOpportunityQueryTuning,
} from "../constants/persona-opportunity-tuning-defaults.js";
import type {
  CreatePersonaSetFormValues,
  PersonaOpportunityQueryTuning,
} from "../types/persona-opportunities.types.js";

function isPositiveInteger(value: number | undefined): value is number {
  return typeof value === "number" && Number.isInteger(value) && value > 0;
}

function hasCommittedDatePair(form: Pick<CreatePersonaSetFormValues, "startDate" | "endDate">): boolean {
  return isValidYmd(form.startDate) && isValidYmd(form.endDate) && form.startDate <= form.endDate;
}

export function createDefaultPersonaSetName(referenceDate = new Date()): string {
  const year = String(referenceDate.getFullYear()).slice(-2);
  const month = String(referenceDate.getMonth() + 1).padStart(2, "0");
  const day = String(referenceDate.getDate()).padStart(2, "0");
  const hours = String(referenceDate.getHours()).padStart(2, "0");
  const minutes = String(referenceDate.getMinutes()).padStart(2, "0");
  return `Set ${year}${month}${day}-${hours}${minutes}`;
}

export function createDefaultCreatePersonaSetFormValues(
  referenceDate = new Date(),
): CreatePersonaSetFormValues {
  const window = resolveRollingWindow(PERSONA_OPPORTUNITY_DEFAULT_DAYS_BACK, referenceDate);
  return {
    name: createDefaultPersonaSetName(referenceDate),
    industry: "",
    industryCompetitors: [],
    adPlatform: "",
    personaIds: [],
    startDate: window.start,
    endDate: window.end,
    activePresetId: DEFAULT_PERSONA_DATA_WINDOW_PRESET,
    dataWindowPreset: DEFAULT_PERSONA_DATA_WINDOW_PRESET,
    customDaysBack: undefined,
    showAdvanced: false,
    advanced: createDefaultPersonaOpportunityQueryTuning(),
  };
}

export function resolveDaysBack(form: Pick<CreatePersonaSetFormValues, "dataWindowPreset" | "customDaysBack">): number | null {
  if (form.dataWindowPreset === "custom") {
    return isPositiveInteger(form.customDaysBack) ? form.customDaysBack : null;
  }
  return PERSONA_DATA_WINDOW_DAYS[form.dataWindowPreset];
}

export function isQueryTuningValid(tuning: PersonaOpportunityQueryTuning): boolean {
  return Object.values(tuning).every(isPositiveInteger);
}

export type CreatePersonaSetRequirement =
  | "name"
  | "industry"
  | "competitors"
  | "adPlatform"
  | "personas"
  | "dataWindow"
  | "advanced";

/**
 * Ordered to match the form, so a summary of what is outstanding reads top-to-bottom
 * the way the fields do. `isFormComplete` derives from this rather than repeating the
 * checks, which is what keeps a disabled Generate button and the reason offered beside
 * it from ever disagreeing.
 */
export function missingRequirements(
  form: CreatePersonaSetFormValues,
): CreatePersonaSetRequirement[] {
  const missing: CreatePersonaSetRequirement[] = [];

  if (!SetNameSchema.safeParse(form.name).success) missing.push("name");
  if (!form.industry.trim()) missing.push("industry");
  if (form.industryCompetitors.length < 1) missing.push("competitors");
  if (!form.adPlatform.trim()) missing.push("adPlatform");
  if (form.personaIds.length < 1) missing.push("personas");
  if (!hasCommittedDatePair(form)) missing.push("dataWindow");

  if (form.showAdvanced && !isQueryTuningValid(form.advanced)) {
    missing.push("advanced");
  }

  return missing;
}

export function isFormComplete(form: CreatePersonaSetFormValues): boolean {
  return missingRequirements(form).length === 0;
}

export function buildQueryTuningFromForm(form: CreatePersonaSetFormValues): PersonaOpportunityQueryTuning {
  if (!form.showAdvanced) {
    return createDefaultPersonaOpportunityQueryTuning();
  }

  return { ...form.advanced };
}
